import {
  SensitiveDataByRegion,
  VisibilityDataApiGetSensitiveDataByRegionRequest,
} from '@ariksa/data-scanning';
import {
  DataSecurityIdentityPage,
  DataFootprintAnalyzed,
  DataTypesApiSyncLabelsRequest,
  PageDocumentClassificationAggregateInfo,
  PageDocumentClassificationInfo,
  PageResourcesRead,
  PageSensitiveDataInfo,
  PageSourceBySensitiveData,
  ResourceDistributionCount,
  ResourceGroup,
  ResourcesApiGetDataTypeSensitiveSourcesRequest,
  ResourcesApiGetResourceGroupsRequest,
  ResourcesApiGetResourcesByGroupRequest,
  ResourcesApiGetResourcesCategoriesCountRequest,
  ResourcesApiGetSensitiveDataSourcesCountRequest,
  ResourcesApiGetSourcesDistributionCountRequest,
  SensitiveDataApiGetFilesBySensitiveDataRequest,
  SensitiveDataApiGetSensitiveDataLabelsCountRequest,
  SensitiveDataByAccount,
  VisibilityDataApiGetDocumentClassificationBySubtypeAndSourceRequest,
  VisibilityDataApiGetDocumentClassificationBySubtypesRequest,
  VisibilityDataApiGetDocumentClassificationByTypeAndSourceRequest,
  VisibilityDataApiGetDocumentClassificationByTypesRequest,
  VisibilityDataApiGetSensitiveDataByAccountRequest,
  VisibilityDataApiGetSensitiveDataInfoTableRequest,
} from '@ariksa/data-scanning/api';
import {
  CloudInsightApiGetRuntimeInsightsForDataSourcesRequest,
  ContextApiGetRiskContextResDataScanRequest,
  InsightApiGetDataSecurityRequest,
  RiskContextResponse,
  RuntimeInsightBySources,
} from '@ariksa/inventory-core/api';
import { AlertsApiGetAlertingResourcesCountRequest } from '@ariksa/notification/api';
import { call } from 'redux-saga/effects';
import { QueryContext } from 'services/utils/QueryContext';
import { takeEveryAction } from 'services/utils/takeEveryAction';
import { takeLatestAction } from 'services/utils/takeLatestAction';

import {
  DataScanningService,
  InventoryService,
  NotificationService,
} from 'api/services';
import { doGetDataSourcesPieChartData } from 'containers/Dashboard/DataSecurityOverview/saga';
import {
  doGetPubliclyExposedDataSources,
  doGetSummaryOfAllDataSources,
} from 'containers/Dashboard/Overview/saga';
import { doGetDataTypes as doGetDataClassifiers } from 'containers/Setup/DataConfiguration/saga';

import { actions } from './slice';

export function* dataDashboardSaga() {
  yield takeLatestAction(
    actions.getDataSecurityIdentities,
    doGetDataSecurityIdentities,
  );
  yield takeLatestAction(actions.getDataSources, doGetDataSources);
  yield takeLatestAction(actions.getDataTypes, doGetDataTypes);
  yield takeLatestAction(
    actions.getSensitiveDataByRegion,
    doGetSensitiveDataByRegion,
  );
  yield takeLatestAction(
    actions.getDataSourcesByRegion,
    doGetSensitiveDataByRegion,
  );
  yield takeLatestAction(
    actions.getRiskContextForDataTypes,
    doGetRiskContextForDataTypes,
  );
  yield takeLatestAction(
    actions.getDataSourcesPieChartData,
    doGetDataSourcesPieChartData,
  );
  yield takeLatestAction(actions.getSourcesByAccounts, doGetSourcesByAccounts);
  yield takeLatestAction(actions.getAnalyzedSources, doGetAnalyzedSources);
  yield takeLatestAction(actions.getAlertingCount, doGetAlertingCount);
  yield takeLatestAction(
    actions.getSensitiveDataLabelCount,
    doGetSensitiveDataLabelCount,
  );
  yield takeEveryAction(
    actions.getSensitiveRecordsForResource,
    doGetSensitiveDataLabelCount,
  );
  yield takeLatestAction(
    actions.getSensitiveDataSourceCount,
    doGetSensitiveDataSourceCount,
  );
  yield takeLatestAction(
    actions.getPubliclyExposedDataSources,
    doGetPubliclyExposedDataSources,
  );
  yield takeLatestAction(
    actions.getSummaryOfAllDataSources,
    doGetSummaryOfAllDataSources,
  );
  yield takeLatestAction(actions.getFilesByDataType, doGetFilesByDataType);
  yield takeLatestAction(actions.getDataTypeSources, doGetDataTypeSources);
  yield takeLatestAction(
    actions.getSensitiveSourcesByDataType,
    doGetDataTypeSources,
  );
  yield takeLatestAction(actions.getDocumentTypes, doGetDocumentTypes);
  yield takeLatestAction(actions.getDocumentSubTypes, doGetDocumentSubTypes);
  yield takeLatestAction(
    actions.getSensitiveDataByAgnosticSources,
    doGetSensitiveAgnosticDataSourcesCount,
  );
  yield takeLatestAction(
    actions.getAllDataResourceTypes,
    doGetDataResourceTypes,
  );
  yield takeLatestAction(
    actions.getDataSourcesByDocumentType,
    doGetDataSourcesByDocumentType,
  );
  yield takeLatestAction(
    actions.getDataSourcesByDocumentSubType,
    doGetDataSourcesByDocumentSubType,
  );
  yield takeLatestAction(
    actions.getDataSourcesByResourceType,
    doGetDataSourcesByResourceType,
  );
  yield takeLatestAction(
    actions.getHighRiskDataSources,
    doGetHighRiskDataSources,
  );
  yield takeLatestAction(actions.getRuntimeInsights, doGetRuntimeInsights);
  yield takeLatestAction(actions.getDataClassifiers, doGetDataClassifiers);
  yield takeLatestAction(actions.syncLabels, doSyncLabels);
}

export function* doGetDataSecurityIdentities(
  ctx: QueryContext<DataSecurityIdentityPage, InsightApiGetDataSecurityRequest>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Insight.getDataSecurity(ctx.params),
    { errorMsg: 'Failed to get identities with sensitive data access!' },
  );
}

export function* doGetDataSources(
  ctx: QueryContext<
    PageResourcesRead,
    ResourcesApiGetDataTypeSensitiveSourcesRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => DataScanningService.Resources.getDataTypeSensitiveSources(ctx.params),
    { errorMsg: 'Failed to get data sources!' },
  );
}

export function* doGetDataTypes(
  ctx: QueryContext<
    PageSensitiveDataInfo,
    VisibilityDataApiGetSensitiveDataInfoTableRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => DataScanningService.Visibility.getSensitiveDataInfoTable(ctx.params),
    { errorMsg: 'Failed to get data types!', cacheKey: 'dataTypes' },
  );
}

export function* doGetDataResourceTypes(
  ctx: QueryContext<ResourceGroup[], ResourcesApiGetResourceGroupsRequest>,
) {
  yield call(
    ctx.fetch,
    () => DataScanningService.Resources.getResourceGroups(ctx.params),
    { errorMsg: 'Failed to get data resource types!' },
  );
}

export function* doGetDocumentTypes(
  ctx: QueryContext<
    PageDocumentClassificationAggregateInfo,
    VisibilityDataApiGetDocumentClassificationByTypesRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      DataScanningService.Visibility.getDocumentClassificationByTypes(
        ctx.params,
      ),
    { errorMsg: 'Failed to get document types!', cacheKey: 'documentTypes' },
  );
}

export function* doGetDocumentSubTypes(
  ctx: QueryContext<
    PageDocumentClassificationAggregateInfo,
    VisibilityDataApiGetDocumentClassificationBySubtypesRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      DataScanningService.Visibility.getDocumentClassificationBySubtypes(
        ctx.params,
      ),
    { errorMsg: 'Failed to get document types!' },
  );
}

export function* doGetDataSourcesByDocumentType(
  ctx: QueryContext<
    PageDocumentClassificationInfo,
    VisibilityDataApiGetDocumentClassificationByTypeAndSourceRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      DataScanningService.Visibility.getDocumentClassificationByTypeAndSource(
        ctx.params,
      ),
    { errorMsg: 'Failed to get data sources!' },
  );
}

export function* doGetDataSourcesByResourceType(
  ctx: QueryContext<
    PageResourcesRead,
    ResourcesApiGetResourcesByGroupRequest & {
      readonly searchTerm?: string | null;
    }
  >,
) {
  const { searchTerm, ...request } = ctx.params;

  yield call(
    ctx.fetch,
    () =>
      DataScanningService.Resources.getResourcesByGroup(request, {
        params: searchTerm ? { search_term: searchTerm } : undefined,
      }),
    { errorMsg: 'Failed to get data sources!' },
  );
}

export function* doGetDataSourcesByDocumentSubType(
  ctx: QueryContext<
    PageDocumentClassificationInfo,
    VisibilityDataApiGetDocumentClassificationBySubtypeAndSourceRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      DataScanningService.Visibility.getDocumentClassificationBySubtypeAndSource(
        ctx.params,
      ),
    { errorMsg: 'Failed to get data sources!' },
  );
}

export function* doGetRiskContextForDataTypes(
  ctx: QueryContext<
    Record<string, RiskContextResponse[]>,
    ContextApiGetRiskContextResDataScanRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Context.getRiskContextResDataScan(ctx.params),
    { errorMsg: 'Failed to get risk context!' },
  );
}

export function* doGetSensitiveDataByRegion(
  ctx: QueryContext<
    SensitiveDataByRegion,
    VisibilityDataApiGetSensitiveDataByRegionRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => DataScanningService.Visibility.getSensitiveDataByRegion(ctx.params),
    {
      errorMsg: 'Failed to get sensitive data by region!',
      cacheKey: 'sensitiveDataByRegion',
    },
  );
}

export function* doGetSourcesByAccounts(
  ctx: QueryContext<
    SensitiveDataByAccount,
    VisibilityDataApiGetSensitiveDataByAccountRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => DataScanningService.Visibility.getSensitiveDataByAccount(ctx.params),
    {
      errorMsg: 'Failed to get sources by accounts!',
      cacheKey: 'sourcesByAccount',
    },
  );
}

export function* doGetAnalyzedSources(
  ctx: QueryContext<
    DataFootprintAnalyzed,
    ResourcesApiGetResourcesCategoriesCountRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => DataScanningService.Resources.getResourcesCategoriesCount(ctx.params),
    {
      errorMsg: 'Failed to get analyzed sources!',
      cacheKey: 'analyzedDataSourcesCount',
    },
  );
}

export function* doGetSensitiveDataSourceCount(
  ctx: QueryContext<number, ResourcesApiGetSensitiveDataSourcesCountRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      DataScanningService.Resources.getSensitiveDataSourcesCount(ctx.params),
    {
      errorMsg: 'Failed to get sensitive data source count!',
      cacheKey: 'sensitiveDataSourceCount',
    },
  );
}

export function* doGetSensitiveDataLabelCount(
  ctx: QueryContext<
    Record<string, number>,
    SensitiveDataApiGetSensitiveDataLabelsCountRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      DataScanningService.SensitiveData.getSensitiveDataLabelsCount(ctx.params),
    {
      errorMsg: 'Failed to get sensitive data label count!',
      cacheKey: 'sensitiveDataLabelCount',
    },
  );
}

export function* doGetDataTypeSources(
  ctx: QueryContext<
    PageResourcesRead,
    ResourcesApiGetDataTypeSensitiveSourcesRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => DataScanningService.Resources.getDataTypeSensitiveSources(ctx.params),
    {
      errorMsg: 'Failed to get sources for data type!',
    },
  );
}

export function* doGetFilesByDataType(
  ctx: QueryContext<
    PageSourceBySensitiveData,
    SensitiveDataApiGetFilesBySensitiveDataRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => DataScanningService.SensitiveData.getFilesBySensitiveData(ctx.params),
    { errorMsg: 'Failed to get files by data type!' },
  );
}

export function* doGetAlertingCount(
  ctx: QueryContext<number, AlertsApiGetAlertingResourcesCountRequest>,
) {
  yield call(
    ctx.fetch,
    () => NotificationService.Alerts.getAlertingResourcesCount(ctx.params),
    {
      errorMsg: 'Failed to get alerting count!',
      cacheKey: 'dataSourcesAlertingCount',
    },
  );
}

export function* doGetSensitiveAgnosticDataSourcesCount(
  ctx: QueryContext<
    ResourceDistributionCount,
    ResourcesApiGetSourcesDistributionCountRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => DataScanningService.Resources.getSourcesDistributionCount(ctx.params),
    {
      errorMsg: 'Failed to get sensitive agnostic data sources count!',
      cacheKey: 'sensitiveAgnosticDataSourcesCount',
    },
  );
}

export function* doGetHighRiskDataSources(
  ctx: QueryContext<
    PageResourcesRead,
    ResourcesApiGetDataTypeSensitiveSourcesRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => DataScanningService.Resources.getDataTypeSensitiveSources(ctx.params),
    {
      errorMsg: 'Failed to get high risk data sources!',
      cacheKey: 'highRiskDataSources',
    },
  );
}

export function* doGetRuntimeInsights(
  ctx: QueryContext<
    RuntimeInsightBySources[],
    CloudInsightApiGetRuntimeInsightsForDataSourcesRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      InventoryService.CloudInsight.getRuntimeInsightsForDataSources(
        ctx.params,
      ),
    {
      errorMsg: 'Failed to get runtime insights for data sources!',
      cacheKey: 'runtimeInsightsDataSources',
    },
  );
}

export function* doSyncLabels(
  ctx: QueryContext<any, DataTypesApiSyncLabelsRequest>,
) {
  yield call(
    ctx.fetch,
    () => DataScanningService.DataTypes.syncLabels(ctx.params),
    {
      errorMsg: 'Failed to sync labels!',
      // Sync only runs on request; the old copy implied it was automatic.
      successMsg: 'Label sync started. Snowflake tags will update shortly.',
    },
  );
}
